/**
 * storePin — which store this builder DOCUMENT was opened on, and whether the account has since moved
 * to another one.
 *
 * ## Why this exists (founder, 2026-09-17: « je change de boutique et le builder m'en montre une autre »)
 *
 * The active store is **server-side state global to the ACCOUNT**, not to a tab: one `store_staff` row
 * per user carries `active = true` (partial unique index `store_staff_one_active_uq`), and
 * `SellerJwtIssuer` re-reads it at every mint. The dashboard's switcher flips that row and reloads
 * ITSELF — but the builder is a different origin, opened in a NEW TAB by every "Customize" click
 * (`window.open(url, '_blank')`), and nothing tells it the ground moved.
 *
 * So a builder tab left open across a switch keeps the token it minted at boot, keeps painting the
 * store the seller has just left, and says nothing. That is the report: the dashboard names the right
 * shop, the editor beside it names another one.
 *
 * The second half is worse and silent. That stale tab's token expires after 15 minutes; the next write
 * 401s, `withAuthRetry` refreshes from the shared cookie, and the token it gets back names the **new**
 * store — so the tab carries on saving the page it is showing into a shop it is not showing. A theme
 * written over another store's live pages is not a glitch the seller can undo.
 *
 * ## The contract
 *
 * The pin is taken ONCE, at boot, from the token the builder actually authenticated with. From then on
 * two things can prove the account has moved:
 *
 *  - {@link tokenLeftThePin} — a refreshed token names a different store. Checked in the ONE place that
 *    refreshes (`withAuthRetry`), which then refuses to replay the request against the other shop.
 *  - an explicit read of the account's current active store ({@link markStoreDrifted}'s caller,
 *    `storeWatch`), which catches the common case — a tab that was simply sitting there — without
 *    waiting for a token to expire.
 *
 * Drift is a ONE-WAY latch: once the account has moved, this document can never be trusted again, and
 * the only honest way out is a reload (which re-mints on the store the seller is really in). Nothing
 * here is a security boundary — the backend derives the tenant from the JWT it verifies, as always.
 * This module exists so the editor stops showing, and stops writing, a shop that is no longer the one.
 */
import { getStoreId } from './session';

/** The store the account has moved INTO. `name` is null when only the id could be established. */
export interface DriftedStore {
  readonly id: string | null;
  readonly name: string | null;
}

let pinned: string | null = null;
let drifted: DriftedStore | null = null;
const listeners = new Set<() => void>();

/**
 * Record the store this document authenticated on. Idempotent on purpose: it must capture the BOOT
 * token, never a later one — a second call after a refresh would re-pin onto the store we are trying
 * to detect. Call it once, right after the boot gate opens.
 */
export function pinStore(): void {
  if (pinned !== null) return;
  pinned = getStoreId();
}

/** The store id this document was opened on, or null (no token / no claim — then nothing is checked). */
export function pinnedStoreId(): string | null {
  return pinned;
}

/**
 * Does the CURRENT in-memory token name a store other than the pinned one? Both sides must be known:
 * a seller with no store yet carries no `store_id` claim, and "absent" is not "different".
 */
export function tokenLeftThePin(): boolean {
  const now = getStoreId();
  return pinned !== null && now !== null && now !== pinned;
}

/**
 * Latch the drift and wake the UI. First call wins — a later, better-informed one must not overwrite
 * the store name already on screen, and the state is terminal anyway.
 */
export function markStoreDrifted(now: DriftedStore): void {
  if (drifted !== null) return;
  drifted = now;
  for (const listener of [...listeners]) listener();
}

/** The store the account moved into, or null while this document is still on its own store. */
export function driftedStore(): DriftedStore | null {
  return drifted;
}

export function isStoreDrifted(): boolean {
  return drifted !== null;
}

/** Subscribe to the latch (one-shot in practice — it never un-latches). Returns the unsubscribe. */
export function subscribeStoreDrift(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam ONLY: module state is per-document in the browser and never reset there. */
export function resetStorePinForTests(): void {
  pinned = null;
  drifted = null;
  listeners.clear();
}
